import {
  Box,
  Button,
  MenuItem,
  Popover,
  TextField,
  Typography,
} from "@mui/material";
import { CasLoader } from "../../brand/CasLoader";
import React, { useEffect, useMemo, useState } from "react";
import { debounce } from "lodash";
import { useAppIdMap } from "../../../hook/app";
import { usePackage, useSetPackageDescription, useSetPackageName, useFetchPackageListener } from "../../../hook/package";
import { pageSearch } from "../../../api/page";
import { setPackageDocs } from "../../../api/package";
import { PackageBlock } from "../../../types/notebook/note/block/package";
import { NotebookPage } from "../../../types/note";
import { CopyClipboard } from "../../copyClipboard";
import { DynamicTextField } from "../../dynamicTextField";
import { NoteBlockContainer } from "../../note";
import { Link } from "@mui/material";
import { useNotebookPagePath } from "../../../hook/page";

const DocPagePicker: React.FC<{
  packageId: string;
  currentDocsPageId?: string;
}> = ({ packageId, currentDocsPageId }) => {
  const { appId } = useAppIdMap();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [selectedPageId, setSelectedPageId] = useState<string | undefined>(
    currentDocsPageId && currentDocsPageId !== "none" ? currentDocsPageId : undefined,
  );
  const [selectedPageName, setSelectedPageName] = useState<string | undefined>(undefined);
  const [searchString, setSearchString] = useState<string>("");
  const [results, setResults] = useState<NotebookPage[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const open = Boolean(anchorEl);

  const handleSearch = useMemo(
    () =>
      debounce((s: string, aid: string) => {
        setIsLoading(true);
        pageSearch({ "app-id": aid, "search-string": s, limit: 20 }).then((pages) => {
          setResults(pages ?? []);
          setIsLoading(false);
        });
      }, 200),
    [],
  );

  useEffect(() => {
    if (!open || !appId) return;
    handleSearch(searchString, appId);
  }, [searchString, open, appId]);

  const handleSelect = async (page: NotebookPage) => {
    await setPackageDocs({
      "package-id": packageId,
      "docs-page-id": page["page-id"],
    });
    setSelectedPageId(page["page-id"]);
    setSelectedPageName(page["page-data"]?.name);
    setAnchorEl(null);
  };

  const handleClear = async () => {
    if (!selectedPageId) return;
    await setPackageDocs({
      "package-id": packageId,
      "docs-page-id": "",
    });
    setSelectedPageId(undefined);
    setSelectedPageName(undefined);
  };

  const displayLabel = selectedPageName ?? (selectedPageId ? `Page: ${selectedPageId}` : "Assign Docs Page");

  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 1 }}>
      <Button
        variant="outlined"
        size="small"
        onClick={(e) => setAnchorEl(e.currentTarget)}
      >
        {displayLabel}
      </Button>
      {selectedPageId && (
        <Button size="small" color="warning" onClick={handleClear}>
          Clear
        </Button>
      )}
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      >
        <Box sx={{ p: 2, width: "20rem" }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Typography variant="h6">Select Docs Page</Typography>
            {isLoading && <CasLoader size={20} />}
          </Box>
          <TextField
            fullWidth
            value={searchString}
            onChange={(e) => setSearchString(e.target.value)}
            placeholder="Search pages..."
            sx={{ mt: 2 }}
          />
          <Box sx={{ height: "10rem", overflowY: "auto", mt: 1 }}>
            {results.map((page) => (
              <MenuItem key={page["page-id"]} onClick={() => handleSelect(page)}>
                {page["page-data"]?.name}
              </MenuItem>
            ))}
          </Box>
        </Box>
      </Popover>
    </Box>
  );
};

export const PackageBlockComponent: React.FC<{ block: PackageBlock }> = ({
  block,
}) => {
  const pkg = usePackage(block["package-id"]);
  const setPackageName = useSetPackageName(block["package-id"]);
  const setPackageDescription = useSetPackageDescription(block["package-id"]);
  const packageName = pkg?.name ?? "";
  const packageDescription = pkg?.description ?? "";
  useFetchPackageListener(block["package-id"]);
  const notebookPagePath = useNotebookPagePath();
  const docsLink = pkg?.["docs-page-id"] && pkg["docs-page-id"] !== "none"
    ? notebookPagePath({ pageId: pkg["docs-page-id"] })
    : undefined;

  return (
    <NoteBlockContainer name="Package">
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <Typography>{block["package-id"]}</Typography>
        <CopyClipboard value={block["package-id"]} />
      </Box>
      <DynamicTextField text={packageName} setText={setPackageName} />
      <TextField
        fullWidth
        multiline
        minRows={2}
        placeholder="Description"
        value={packageDescription}
        onChange={(e) => setPackageDescription(e.target.value)}
        sx={{ mt: 1 }}
      />
      <DocPagePicker
        packageId={block["package-id"]}
        currentDocsPageId={pkg?.["docs-page-id"]}
      />
      {docsLink && (
        <Box sx={{ mt: 1 }}>
          <Link href={docsLink} underline="hover">
            Open Docs Page
          </Link>
        </Box>
      )}
    </NoteBlockContainer>
  );
};
